import React, { useCallback, useContext } from "react";
import { Box } from "@mui/material";
import { Feature } from "geojson";

import { MapContext } from "fond/map/MapProvider";

import PopoutHeader from "./PopoutHeader";
import SelectedPaths from "./SelectedPaths";

interface PathPreferencingContentProps {
  features: Feature[];
  returnMode?: string;
}

const PathPreferencingContent: React.FC<PathPreferencingContentProps> = ({ features, returnMode }) => {
  const { map, drawControl } = useContext(MapContext);

  /**
   * When the user cancels or applies changes, return to the active tool.
   */
  const returnToActiveTool = useCallback(() => {
    const mode = returnMode || "simple_select";
    drawControl.current?.changeMode(mode);
    map?.fire("draw.modechange", { type: "draw.modechange", mode, userInitiated: true });
    map?.fire("draw.selectionchange", { features: [] });
  }, [drawControl, map, returnMode]);

  const handleCancel = useCallback(() => {
    returnToActiveTool();
  }, [returnToActiveTool]);

  const handleApply = useCallback(
    (value: string, includeCrossings: boolean) => {
      if (!drawControl.current) return;

      const costFactor = parseFloat(value);

      for (const feature of features) {
        if (includeCrossings || feature.properties?.Crossing !== "YES") {
          const id = feature.id as string;
          drawControl.current.setFeatureProperty(id, "CostFactor", costFactor);
          const updated = drawControl.current.get(id);
          if (updated) {
            drawControl.current.add(updated);
          }
        }
      }

      map?.fire("draw.update", { features: [], action: "change_coordinates" });
      returnToActiveTool();
    },
    [drawControl, map, features, returnToActiveTool]
  );

  return (
    <Box p={1.5} display="flex" flexDirection="column" gap={2}>
      <PopoutHeader onClose={handleCancel} />
      <SelectedPaths features={features} onCancel={handleCancel} onApply={handleApply} />
    </Box>
  );
};

export default PathPreferencingContent;
